import { PPRAStatus } from "../../types";
import { BadgeCheck, Clock, XCircle, Info } from "lucide-react";
import { useState } from "react";

interface VerificationBadgeProps {
  status: PPRAStatus;
  showDisclaimer?: boolean;
  size?: "sm" | "md";
}

const badgeConfig: Record<
  PPRAStatus,
  { label: string; className: string; icon: React.ComponentType<{ className?: string }> }
> = {
  [PPRAStatus.Verified]: {
    label: "PPRA Verified",
    className: "bg-green-500/20 text-green-400 border-green-500/30",
    icon: BadgeCheck,
  },
  [PPRAStatus.Submitted]: {
    label: "Pending Verification",
    className: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
    icon: Clock,
  },
  [PPRAStatus.Rejected]: {
    label: "Not Verified",
    className: "bg-red-500/20 text-red-400 border-red-500/30",
    icon: XCircle,
  },
};

export function VerificationBadge({ status, showDisclaimer, size = "sm" }: VerificationBadgeProps) {
  const config = badgeConfig[status];
  const Icon = config.icon;
  const textSize = size === "sm" ? "text-xs" : "text-sm";
  const iconSize = size === "sm" ? "h-3 w-3" : "h-4 w-4";

  return (
    <div className="relative inline-flex items-center gap-1">
      <span
        className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 font-medium ${textSize} ${config.className}`}
      >
        <Icon className={iconSize} />
        {config.label}
      </span>
      {showDisclaimer && <DisclaimerTooltip />}
    </div>
  );
}

function DisclaimerTooltip() {
  const [show, setShow] = useState(false);

  return (
    <span
      className="relative"
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
    >
      <Info className="h-3 w-3 text-white/30 hover:text-white/60 cursor-help" />
      {show && (
        <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 rounded-lg bg-ink border border-white/10 px-3 py-2 text-[10px] text-white/60 shadow-xl z-50">
          Verification indicates the FFC number supplied matches public PPRA registration information at time of verification. It does not constitute an endorsement or guarantee of service quality.
          <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-ink" />
        </span>
      )}
    </span>
  );
}
